import React, { useEffect, useState, useRef } from 'react'
import {
  StyledLayoutWrapper,
  StyledHeader,
  StyledList,
  StyledListItem,
  StyledSearchInput,
  StyledSearchContainer,
  StyledSearchIcon,
  StyledEllipsisButton,
  StyledDropdown,
  StyledDropdownItem,
  StyledInput,
  StyledHeaderTitle,
  StyledIconButton,
  StyledSessionName,
  StyledEmptyList
} from './styled'
import { Search, MoreHoriz, DeleteOutline, Close } from '@mui/icons-material'
import ExpandIcon from '../../assets/ExpandIcon'
import ShrinkIcon from '../../assets/ShrinkIcon'
import { FaPencil, FaRegTrashCan } from 'react-icons/fa6'
import { useTheme } from '@mui/material/styles'
import { useMediaQuery } from '@mui/material'
import { getAllSessions } from '../../utils/api'
import DeleteSessionConfirmation from './DeleteSessionConfirmation'
import { useAppSelector } from '../../reducers/hooks'
import { useDispatch } from 'react-redux'
import { userReducer } from '../../reducers/user/userReducer'
import getForcedMobile from '../../utils/forceMobile'
import { getIsIframe } from '../../utils/getIsIframe'
import { iframeReducer } from '../../reducers/iframe/iframeReducer'
import { useChatHistory } from '../../features/ChatHistory'

interface ChatHistoryLayoutProps {
  inDrawer?: boolean
  onSessionSelect?: () => void
  onClose?: () => void
}

const ChatHistoryLayout: React.FC<ChatHistoryLayoutProps> = ({ inDrawer = false, onSessionSelect, onClose }) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const forcedMobile = getForcedMobile()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isIframe = getIsIframe()
  const { refreshSessions, saveRename, handleDeleteSession } = useChatHistory()
  const userEmail = useAppSelector((state) => state.user.email) ?? ''
  const platformId = useAppSelector((state) => state.user.platformId)?.toString() ?? ''
  const platform = useAppSelector((state) => state.user.platform)
  const chatHistory = useAppSelector((state) => state.user.chatHistory)
  const chatbotExpanded = useAppSelector((state) => state.iframe.chatExpanded) ?? false
  const showInitialScreen = useAppSelector((state) => state.user.showInitialScreen) ?? true
  const hideExpandIcon = inDrawer || localStorage.getItem('hide_expand_icon') === 'true' ? true : (isMobile || forcedMobile) || !isIframe
  const [filteredSessions, setFilteredSessions] = useState<any[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [chatName, setChatName] = useState('')
  const [editNameFor, setEditNameFor] = useState<string | null>(null)
  const editInputRef = useRef<HTMLInputElement | null>(null)
  const [showMoreIconFor, setShowMoreIconFor] = useState<string | null>(null)
  const [openDropdownFor, setOpenDropdownFor] = useState<string | null>(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null)
  const currentSession = useAppSelector((state) => state.user.currentSession)

  const selectSession = (sessionId: string): void => {
    dispatch(userReducer.actions.changeSessionId(sessionId))
    if (onSessionSelect !== undefined) {
      onSessionSelect()
    }
  }

  useEffect(() => {
    // API call for chat history
    if (platform !== null && platformId !== null && userEmail !== '') {
      getAllSessions(platform, platformId, userEmail, '50').then((res) => {
        dispatch(userReducer.actions.changeChatHistory(res.payload))
      }).catch((err) => {
        console.log('Error fetching sessions:', err)
      })
    }
  }, [platform, platformId, userEmail])

  useEffect(() => {
    const filteredSessions = chatHistory?.filter((session) => session.name?.toLowerCase().includes(searchTerm.toLowerCase()))
    setFilteredSessions(filteredSessions ?? [])
  }, [searchTerm, chatHistory])

  const onChangeChatbotSize = (): void => {
    chatbotExpanded ? dispatch(iframeReducer.actions.changeChatExpanded(false)) : dispatch(iframeReducer.actions.changeChatExpanded(true))
    chatbotExpanded ? parent.postMessage('shrinkChatbotWindow', '*') : parent.postMessage('expandChatbotWindow', '*')
    chatbotExpanded && window.isfromRN === true ? parent.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'shrinkChatbotWindow', data: '*' })) : parent.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'expandChatbotWindow', data: '*' }))
  }

  const openDeleteModal = (sessionId: string): void => {
    setSessionToDelete(sessionId)
    setDeleteModalOpen(true)
    setOpenDropdownFor(null)
  }

  const closeDeleteModal = (): void => {
    setDeleteModalOpen(false)
    setSessionToDelete(null)
  }

  const handleSaveRename = async (sessionId: string, sessionName: string): Promise<void> => {
    await saveRename(sessionId, sessionName).catch(console.error)
    setEditNameFor(null)
  }

  useEffect(() => {
    if (!showInitialScreen) {
      window.setTimeout(() => {
        refreshSessions().catch(console.error)
      }, 250)
    }
  }, [showInitialScreen])

  useEffect(() => {
    if (editNameFor !== null) {
      setTimeout(() => {
        try {
          setOpenDropdownFor(null)
          editInputRef.current?.focus()
          // move cursor to end
          const len = editInputRef.current?.value.length ?? 0
          editInputRef.current?.setSelectionRange(0, len)
        } catch (e) {
          // ignore
        }
      }, 10)
    }
  }, [editNameFor])

  return (
    <StyledLayoutWrapper isMobile={isMobile || forcedMobile} inDrawer={inDrawer}>
      <StyledHeader showHamburger={inDrawer}>
        {chatbotExpanded && !hideExpandIcon &&
          <StyledIconButton size="large" title='Minimize Chat Window' onClick={() => { onChangeChatbotSize() }}>
            <ShrinkIcon fontSize="medium"/>
          </StyledIconButton>
        }
        {!chatbotExpanded && !hideExpandIcon &&
          <StyledIconButton size="large" title='Expand Chat Window' onClick={() => { onChangeChatbotSize() }}>
            <ExpandIcon fontSize="medium"/>
          </StyledIconButton>
        }
        <StyledHeaderTitle>
          Chat History
        </StyledHeaderTitle>
        {inDrawer && onClose !== undefined &&
          <StyledIconButton size="large" title='Close' className='closeButton' onClick={onClose}>
            <Close fontSize="medium"/>
          </StyledIconButton>
        }
      </StyledHeader>
      <StyledSearchContainer>
        <StyledSearchIcon>
          <Search />
        </StyledSearchIcon>
        <StyledSearchInput value={searchTerm} onChange={(e) => { setSearchTerm(e.currentTarget.value) }} placeholder="Search chats..." />
      </StyledSearchContainer>
      <StyledList>
        {searchTerm.length > 0
          ? filteredSessions.map((session) => (
            <StyledListItem key={session.session_id} className={session.session_id === currentSession?.session_id ? 'selectedSession' : ''} onMouseEnter={() => { setShowMoreIconFor(session.session_id) }} onMouseLeave={() => { setShowMoreIconFor(null); setOpenDropdownFor(null) }} onClick={() => { selectSession(session.session_id) }}>
              {editNameFor === session.session_id
                ? <StyledInput ref={editInputRef} type="text" id='editNameInput' defaultValue={session.name} value={chatName} onChange={(e) => { setChatName(e.currentTarget.value) }} onBlur={(e) => { handleSaveRename(session.session_id, chatName).catch(console.error) }} onKeyDown={(e) => { if (e.key === 'Enter') { handleSaveRename(session.session_id, chatName).catch(console.error) } }} />
                : <StyledSessionName style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} onClick={() => { selectSession(session.session_id) }}>
                  {session.name ?? 'Unnamed Session'}
                </StyledSessionName>
              }
              {showMoreIconFor === session.session_id &&
              <StyledEllipsisButton className="ellipsisButton" aria-label="actions" onClick={() => { setOpenDropdownFor(openDropdownFor === session.session_id ? null : session.session_id) }}>
                <MoreHoriz />
              </StyledEllipsisButton>
              }
              {openDropdownFor === session.session_id &&
              <StyledDropdown>
                <StyledDropdownItem onClick={() => { setChatName(session.name ?? ''); setEditNameFor(session.session_id) }}><FaPencil /> Rename</StyledDropdownItem>
                <StyledDropdownItem className='deleteMenuItem' onClick={() => { openDeleteModal(session.session_id) }}><DeleteOutline /> Delete</StyledDropdownItem>
              </StyledDropdown>
              }
            </StyledListItem>
          ))
          : chatHistory?.map((session) => (
            <StyledListItem key={session.session_id} className={session.session_id === currentSession?.session_id ? 'selectedSession' : ''} onMouseEnter={() => { setShowMoreIconFor(session.session_id) }} onMouseLeave={() => { setShowMoreIconFor(null); setOpenDropdownFor(null) }}>
              {editNameFor === session.session_id
                ? <StyledInput ref={editInputRef} type="text" id='editNameInput' defaultValue={session.name} value={chatName} onChange={(e) => { setChatName(e.currentTarget.value) }} onBlur={() => { handleSaveRename(session.session_id, chatName).catch(console.error) }} onKeyDown={(e) => { if (e.key === 'Enter') { handleSaveRename(session.session_id, chatName).catch(console.error) } }} />
                : <StyledSessionName style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} onClick={() => { selectSession(session.session_id) }}>
                  {session.name ?? 'Unnamed Session'}
                </StyledSessionName>
              }
              {showMoreIconFor === session.session_id &&
              <StyledEllipsisButton className="ellipsisButton" aria-label="actions" onClick={() => { setOpenDropdownFor(openDropdownFor === session.session_id ? null : session.session_id) }}>
                <MoreHoriz />
              </StyledEllipsisButton>
              }
              {openDropdownFor === session.session_id &&
              <StyledDropdown>
                <StyledDropdownItem onClick={() => { setChatName(session.name ?? ''); setEditNameFor(session.session_id) }}><FaPencil /> Rename</StyledDropdownItem>
                <StyledDropdownItem className='deleteMenuItem' onClick={() => { openDeleteModal(session.session_id) }}><FaRegTrashCan /> Delete</StyledDropdownItem>
              </StyledDropdown>
              }
            </StyledListItem>
          ))}
        {(chatHistory?.length === 0 || filteredSessions?.length === 0) &&
          <StyledEmptyList>
            No chat sessions found.
          </StyledEmptyList>
        }
      </StyledList>
      <DeleteSessionConfirmation
        open={deleteModalOpen}
        sessionName={chatHistory?.find(s => s.session_id === sessionToDelete)?.name ?? null}
        onClose={closeDeleteModal}
        onConfirmation={() => { if (sessionToDelete !== null) { handleDeleteSession(sessionToDelete).catch(console.error); closeDeleteModal() } }}
      />
    </StyledLayoutWrapper>
  )
}

export default ChatHistoryLayout
